import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import type { Product } from '../core/types/catalog';
import { useCatalog } from './CatalogContext';
import { useSite } from './SiteContext';
import { codesIn, findCodeIn } from '../data/codes';
import type { AttributionCode } from '../core/types/site';
import type { OrderLineVia } from '../core/types/requests';
import { giftChoiceFor, nextTierIn, tierIn, unlockedIn, type GiftTier } from '../data/gifts';
import { freeShippingLine, policyCopy } from '../data/policies';
import { founder } from '../data/content';
import { readLocalJson, writeLocalJson } from '../core/storage';
import { toCents } from '../core/format/currency';
import { clampQty, isPhysical, isSoldOut, maxQty } from '../core/rules/stock';
import { canSubscribe } from '../core/rules/features';
import { currentFeatures } from '../data/features';
import { useFeatures } from '../hooks/useFeatures';
import { track } from '../utils/analytics';
export type CartLine = {
  product: Product;
  qty: number;
  /** Bewust Abonnement: zelfde prijs, andere voorwaarden. */
  subscribe: boolean;
  routineId?: string;
  /** Kwam uit het huidadvies (Inzicht › Webshop). Blijft staan als ze het later nog eens toevoegt. */
  via?: OrderLineVia;
};

/** Alleen een bekende herkomst overnemen uit de opgeslagen mand. */
const viaOf = (v: unknown): {
  via?: OrderLineVia;
} => v === 'huidadvies' ? {
  via: v
} : {};

/**
 * Geen eigen kortingen of coupons: de regelprijs is `product.price`, precies het bedrag dat
 * de server rekent (bij een goedgekeurde leveranciersactie de actieprijs). Zo lopen
 * mand, verzendgrens en cadeaustaffel gelijk met de server. Wijkt het toch af, dan weigert
 * de server de bestelling via `expectedTotalCents` (zie useCheckout).
 */
function lineUnitPrice(line: CartLine) {
  return line.product.price;
}
export type { AttributionCode };

/**
 * De winkelstate is opgesplitst in vijf contexts, zodat een component alleen opnieuw
 * tekent als het stuk dat hij gebruikt verandert:
 *
 *  - useCart()           → inhoud en bedragen (winkelmand, afrekenen)
 *  - useCartCount()      → alleen het aantal (badge in de navigatie)
 *  - useCartActions()    → toevoegen, aantal, verwijderen… (stabiel: tekent nooit opnieuw)
 *  - useOverlays()       → welke lade open is
 *  - useOverlayActions() → lades openen/sluiten (stabiel)
 *
 * Een productkaart gebruikt dus alleen useCartActions() en blijft staan als je iets
 * toevoegt; de navigatie tekent alleen de badge opnieuw.
 */
export type CartState = {
  lines: CartLine[];
  count: number;
  subtotal: number;
  /** Subtotaal zonder cadeaubonnen: telt voor gratis verzending en de cadeaustaffel. */
  giftSubtotal: number;
  shipping: number;
  total: number;
  hasSubscription: boolean;
  unlockedGift: GiftTier | null;
  /** Alle vrijgespeelde cadeaus (zonder gratis verzending), met de keuze van de klant. */
  gifts: {
    tier: GiftTier;
    choice: string | null;
  }[];
  nextGift: GiftTier | null;
  giftTiers: GiftTier[];
  appliedCode: AttributionCode | null;
  /** Wat we bij het bijwerken van de mand aanpasten (product weg, aantal omlaag); leeg = niets te melden. */
  adjustments: CartAdjustment[];
};
export type CartActions = {
  /** `false` als er niets bij kwam (uitverkocht of maximum bereikt); de melding regelt `add` zelf. */
  add: (product: Product, opts?: {
    subscribe?: boolean;
    silent?: boolean;
    via?: OrderLineVia;
  }) => boolean;
  addRoutine: (routineId: string, opts?: {
    via?: OrderLineVia;
  }) => void;
  toggleSubscribe: (id: string) => void;
  setQty: (id: string, qty: number) => void;
  remove: (id: string) => void;
  clearCart: () => void;
  applyCode: (code: string) => boolean;
  removeCode: () => void;
  /** Bijv. welk reisformaat. Per cadeauregel (`ruleId`). */
  chooseGift: (ruleId: string, choice: string) => void;
  /** De melding "We hebben je mand bijgewerkt" wegklikken. */
  dismissAdjustments: () => void;
};
export type OverlayState = {
  cartOpen: boolean;
  /** Slug van de behandeling waarmee de afsprakenlade opent. */
  bookingTreatment: string | null;
  bookingOpen: boolean;
  quizOpen: boolean;
};
export type OverlayActions = {
  openCart: () => void;
  closeCart: () => void;
  openBooking: (treatmentSlug?: string) => void;
  closeBooking: () => void;
  openQuiz: () => void;
  closeQuiz: () => void;
  /** Alle lades dicht (bij een paginawissel). */
  closeAll: () => void;
};
const CartContext = createContext<CartState | null>(null);
const CartCountContext = createContext<number | null>(null);
const CartActionsContext = createContext<CartActions | null>(null);
const OverlayContext = createContext<OverlayState | null>(null);
const OverlayActionsContext = createContext<OverlayActions | null>(null);

/**
 * De winkelmand wordt bewaard als ids + aantallen — nooit als productkopie.
 * Bij terugkomst worden prijs en naam dus altijd uit de actuele catalogus gehaald.
 * Valt er iets uit of gaat een aantal omlaag, dan onthouden we dat als `adjustments`:
 * de mand en het afrekenen tonen er één melding over.
 */
const CART_KEY = 'bb-cart-v1';
type StoredCart = {
  lines: {
    productId: string;
    qty: number;
    subscribe: boolean;
    routineId?: string;
    via?: OrderLineVia;
  }[];
  code: string | null;
  giftChoices?: Record<string, string>;
};
const readStoredCart = () => readLocalJson<StoredCart | null>(CART_KEY, null);

/**
 * De bewaarde mand (gedeeld tussen tabbladen), los van de catalogus: voor de terugkeerpagina, die
 * moet weten of de mand nog bij de betaalde bestelling hoort vóórdat de catalogus geladen is.
 */
export function storedCartLines(): {
  productId: string;
  qty: number;
  subscribe: boolean;
}[] {
  const lines = readStoredCart()?.lines;
  return Array.isArray(lines) ? lines.filter(l => l && typeof l.productId === 'string').map(l => ({
    productId: l.productId,
    qty: Number(l.qty) || 0,
    subscribe: Boolean(l.subscribe)
  })) : [];
}
const parseStoredCart = (raw: string | null): StoredCart | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredCart;
  } catch {
    return null;
  }
};

/**
 * Opgeslagen mand → regels, met een geldig aantal (ook als localStorage beschadigd of aangepast is).
 * Een product dat niet meer bestaat gaat eruit. Een uitverkocht product blijft staan, net als
 * wanneer het tijdens het winkelen uitverkoopt (`clampToStock`): het kan zo weer leverbaar zijn,
 * en de mand en het afrekenen vragen het dan eerst weg te halen. Eén plek, gebruikt bij het
 * opstarten én bij een wijziging vanuit een ander tabblad.
 */
const hydrateLines = (stored: StoredCart | null, productById: (id: string) => Product | undefined): Hydrated => {
  const adjustments: CartAdjustment[] = [];
  const lines = (stored && Array.isArray(stored.lines) ? stored.lines : []).flatMap((l): CartLine[] => {
    const product = productById(l.productId);
    if (!product) {
      // Een onleesbare regel (geen id) is geen product dat de klant koos: niets over melden.
      if (typeof l.productId === 'string' && l.productId) adjustments.push({
        kind: 'removed',
        name: null
      });
      return [];
    }
    if (isSoldOut(product)) {
      adjustments.push({
        kind: 'soldOut',
        name: product.name
      });
      const qty = typeof l.qty === 'number' && Number.isFinite(l.qty) ? Math.max(1, Math.floor(l.qty)) : 1;
      return [{
        product,
        qty,
        subscribe: false,
        routineId: l.routineId,
        ...viaOf(l.via)
      }];
    }
    const qty = clampQty(product, l.qty);
    if (qty > 0 && typeof l.qty === 'number' && l.qty > qty) adjustments.push({
      kind: 'reduced',
      name: product.name,
      qty
    });
    return qty > 0 ? [{
      product,
      qty,
      subscribe: Boolean(l.subscribe) && canSubscribe(currentFeatures(), product),
      routineId: l.routineId,
      ...viaOf(l.via)
    }] : [];
  });
  return {
    lines,
    adjustments
  };
};

/**
 * Komt de catalogus opnieuw binnen met minder voorraad, dan gaat een te hoog aantal omlaag.
 * Uitverkocht blijft wel in de mand staan (met de bestaande "haal eerst weg"-melding),
 * want dat lost de klant zelf op en het kan zo weer leverbaar zijn.
 */
const clampToStock = (lines: CartLine[], productById: (id: string) => Product | undefined): Hydrated => {
  const adjustments: CartAdjustment[] = [];
  const next = lines.map(l => {
    const product = productById(l.product.id);
    if (!product || isSoldOut(product)) return l;
    const qty = Math.min(l.qty, maxQty(product));
    if (qty === l.qty) return l;
    adjustments.push({
      kind: 'reduced',
      name: product.name,
      qty
    });
    return {
      ...l,
      qty
    };
  });
  return {
    lines: adjustments.length ? next : lines,
    adjustments
  };
};

/**
 * Wat er bij het bijwerken van de mand veranderde. `soldOut` = tijdelijk uitverkocht (kan terugkomen);
 * `removed` = het product staat niet meer in het assortiment (geen naam meer bekend).
 */
export type CartAdjustment = {
  kind: 'soldOut';
  name: string;
} | {
  kind: 'removed';
  name: string | null;
} | {
  kind: 'reduced';
  name: string;
  qty: number;
};
type Hydrated = {
  lines: CartLine[];
  adjustments: CartAdjustment[];
};

/** In centen rekenen voorkomt afrondingsverschillen (0,1 + 0,2). */
const cents = toCents;
const sum = (ls: CartLine[]) => ls.reduce((n, l) => n + l.qty * cents(lineUnitPrice(l)), 0) / 100;
export function StoreProvider({
  children
}: {
  children: ReactNode;
}) {
  const {
    routineById,
    routineTotals,
    productById
  } = useCatalog();
  const features = useFeatures();
  // Instellingen als state: verandert de verzendgrens of de staffel, dan rekent de mand opnieuw.
  const site = useSite();
  const {
    policy,
    giftTiers
  } = site;
  const allCodes = useMemo(() => codesIn(site), [site]);
  const findCode = (input: string) => findCodeIn(allCodes, input);
  // Acties zijn stabiel: zij lezen de actuele instellingen via een ref.
  const siteRef = useRef({
    allCodes,
    freeShippingFrom: policy.freeShippingFrom
  });
  siteRef.current = {
    allCodes,
    freeShippingFrom: policy.freeShippingFrom
  };
  const [stored] = useState(readStoredCart);
  const [initial] = useState(() => hydrateLines(stored, productById));
  const [lines, setLines] = useState<CartLine[]>(initial.lines);
  const [adjustments, setAdjustments] = useState<CartAdjustment[]>(initial.adjustments);
  const [cartOpen, setCartOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingTreatment, setBookingTreatment] = useState<string | null>(null);
  const [quizOpen, setQuizOpen] = useState(false);
  const [appliedCode, setAppliedCode] = useState<AttributionCode | null>(() => stored?.code ? findCode(stored.code) : null);
  const [giftChoices, setGiftChoices] = useState<Record<string, string>>(() => stored?.giftChoices ?? {});
  useEffect(() => {
    const data: StoredCart = {
      lines: lines.map(l => ({
        productId: l.product.id,
        qty: l.qty,
        subscribe: l.subscribe,
        routineId: l.routineId,
        ...viaOf(l.via)
      })),
      code: appliedCode?.code ?? null,
      giftChoices
    };
    // Privémodus of volle opslag: de mand werkt gewoon, alleen zonder bewaren.
    writeLocalJson(CART_KEY, data);
  }, [lines, appliedCode, giftChoices]);

  // Meerdere tabbladen: het `storage`-event komt alleen binnen in de ANDERE tabbladen,
  // dus er ontstaat geen lus. Schrijft dit tabblad daarna dezelfde JSON terug, dan
  // verandert de waarde niet en vuurt er ook geen nieuw event. `key === null` = opslag gewist.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== null && e.key !== CART_KEY) return;
      const next = parseStoredCart(e.key === null ? null : e.newValue);
      // Het andere tabblad heeft zijn mand al bijgewerkt (en daar gemeld); hier alleen overnemen.
      setLines(hydrateLines(next, productById).lines);
      setAppliedCode(next?.code ? findCodeIn(siteRef.current.allCodes, next.code) : null);
      setGiftChoices(next?.giftChoices ?? {});
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [productById]);

  // `lines` via een ref lezen, zodat de acties kunnen controleren zonder bij elke wijziging opnieuw te worden gemaakt.
  const linesRef = useRef(lines);
  linesRef.current = lines;

  // Nieuwe catalogus (verversen, of de server na de lokale terugval): aantallen boven de voorraad omlaag, met melding.
  const firstCatalog = useRef(productById);
  useEffect(() => {
    if (firstCatalog.current === productById) return;
    firstCatalog.current = productById;
    const {
      lines: next,
      adjustments: changed
    } = clampToStock(linesRef.current, productById);
    if (!changed.length) return;
    setLines(next);
    setAdjustments(a => [...a.filter(x => !changed.some(c => c.name === x.name)), ...changed]);
  }, [productById]);
  // Eén lade tegelijk: de winkelmand openen sluit het huidadvies en de afsprakenlade
  // (bijv. "Zet dit protocol in mijn winkelmand" of "Bekijk winkelmand" in een melding).
  const showCart = useCallback(() => {
    setCartOpen(true);
    setQuizOpen(false);
    setBookingOpen(false);
  }, []);
  const showCartRef = useRef(showCart);
  showCartRef.current = showCart;

  // Catalogus-opzoekers via een ref: komt de catalogus opnieuw binnen, dan blijven de acties toch stabiel.
  const catalogRef = useRef({
    routineById,
    routineTotals,
    productById
  });
  catalogRef.current = {
    routineById,
    routineTotals,
    productById
  };
  const add = useCallback<CartActions['add']>((product, opts) => {
    // Eén plek voor de voorraadregel: elke "In winkelmand"-knop (kaart, quiz, artikel, upsell…) loopt hierlangs.
    if (isSoldOut(product)) {
      toast(`${product.name} is tijdelijk uitverkocht`, {
        description: `App ${founder.firstName} gerust voor een alternatief.`
      });
      return false;
    }
    const current = linesRef.current.find(l => l.product.id === product.id)?.qty ?? 0;
    if (current >= maxQty(product)) {
      toast(`Er kunnen er maximaal ${maxQty(product)} in je winkelmand`, {
        description: product.name
      });
      return false;
    }
    const subscribe = canSubscribe(currentFeatures(), product) ? opts?.subscribe : false;
    setLines(prev => {
      const existing = prev.find(l => l.product.id === product.id);
      if (existing) {
        return prev.map(l => l.product.id === product.id ? {
          ...l,
          qty: Math.min(l.qty + 1, maxQty(product)),
          subscribe: subscribe ?? l.subscribe,
          ...viaOf(opts?.via ?? l.via)
        } : l);
      }
      return [...prev, {
        product,
        qty: 1,
        subscribe: subscribe ?? false,
        ...viaOf(opts?.via)
      }];
    });
    track('mand-toegevoegd', {
      product: product.id
    });
    if (!opts?.silent) {
      toast(`${product.name} toegevoegd`, {
        description: subscribe ? `Bewust Abonnement: ${policyCopy.subscriptionPerks}` : freeShippingLine(siteRef.current.freeShippingFrom),
        action: {
          label: 'Bekijk winkelmand',
          onClick: () => showCartRef.current()
        }
      });
    }
    return true;
  }, []);
  const addRoutine = useCallback<CartActions['addRoutine']>((routineId, opts) => {
    const routine = catalogRef.current.routineById(routineId);
    if (!routine) return;
    const {
      items: all
    } = catalogRef.current.routineTotals(routine);
    const items = all.filter(p => !isSoldOut(p));
    const skipped = all.filter(isSoldOut);
    if (skipped.length) {
      toast(`${skipped.map(p => p.name).join(', ')} ${skipped.length === 1 ? 'is' : 'zijn'} tijdelijk uitverkocht`, {
        description: items.length ? `De rest van het ${routine.name} staat in je winkelmand. ${founder.firstName} adviseert je graag een alternatief.` : `App ${founder.firstName} voor een alternatief.`
      });
    }
    if (!items.length) return;
    setLines(prev => {
      const next = [...prev];
      items.forEach(product => {
        const idx = next.findIndex(l => l.product.id === product.id);
        if (idx >= 0) {
          next[idx] = {
            ...next[idx],
            routineId,
            ...viaOf(opts?.via ?? next[idx].via)
          };
        } else {
          next.push({
            product,
            qty: 1,
            subscribe: false,
            routineId,
            ...viaOf(opts?.via)
          });
        }
      });
      return next;
    });
    showCartRef.current();
  }, []);
  const toggleSubscribe = useCallback((id: string) => {
    setLines(prev => prev.map(l => l.product.id === id && canSubscribe(currentFeatures(), l.product) ? {
      ...l,
      subscribe: !l.subscribe
    } : l));
  }, []);
  const setQty = useCallback((id: string, qty: number) => {
    // Begrenzen met de actuele catalogus (voorraad kan veranderd zijn sinds het product in de mand kwam),
    // net als de weergave doet. Het product in de regel is alleen de terugval.
    const current = (l: CartLine) => catalogRef.current.productById(l.product.id) ?? l.product;
    const line = linesRef.current.find(l => l.product.id === id);
    if (line && Number.isFinite(qty) && qty > maxQty(current(line))) {
      toast(`Er kunnen er maximaal ${maxQty(current(line))} in je winkelmand`, {
        description: line.product.name
      });
    }
    setLines(prev => prev.flatMap(l => {
      if (l.product.id !== id) return [l];
      const next = clampQty(current(l), qty);
      return next > 0 ? [{
        ...l,
        qty: next
      }] : [];
    }));
  }, []);

  // Weghalen kan per ongeluk (kleine knop, duim): één tik "Ongedaan maken" zet de regel terug op
  // dezelfde plek, met aantal, bijvullen en protocol. Is hij intussen opnieuw toegevoegd, dan niets.
  const remove = useCallback((id: string) => {
    const at = linesRef.current.findIndex(l => l.product.id === id);
    const removed = linesRef.current[at];
    setLines(prev => prev.filter(l => l.product.id !== id));
    if (!removed) return;
    toast(`${removed.product.name} verwijderd`, {
      action: {
        label: 'Ongedaan maken',
        onClick: () => setLines(prev => {
          if (prev.some(l => l.product.id === id)) return prev;
          const next = [...prev];
          next.splice(Math.min(at, next.length), 0, removed);
          return next;
        })
      }
    });
  }, []);
  const applyCode = useCallback((input: string) => {
    const found = findCodeIn(siteRef.current.allCodes, input);
    if (found) {
      setAppliedCode(found);
      return true;
    }
    return false;
  }, []);
  const cartActions = useMemo<CartActions>(() => ({
    add,
    addRoutine,
    toggleSubscribe,
    setQty,
    remove,
    applyCode,
    removeCode: () => setAppliedCode(null),
    chooseGift: (ruleId, choice) => setGiftChoices(c => ({
      ...c,
      [ruleId]: choice
    })),
    dismissAdjustments: () => setAdjustments([]),
    clearCart: () => {
      setLines([]);
      setAppliedCode(null);
      setGiftChoices({});
      setAdjustments([]);
    }
  }), [add, addRoutine, toggleSubscribe, setQty, remove, applyCode]);
  const overlayActions = useMemo<OverlayActions>(() => ({
    openCart: showCart,
    closeCart: () => setCartOpen(false),
    openBooking: (treatment?: string) => {
      setBookingTreatment(treatment ?? null);
      setBookingOpen(true);
      setCartOpen(false);
      setQuizOpen(false);
    },
    closeBooking: () => setBookingOpen(false),
    openQuiz: () => {
      setQuizOpen(true);
      setCartOpen(false);
      setBookingOpen(false);
    },
    closeQuiz: () => setQuizOpen(false),
    closeAll: () => {
      setCartOpen(false);
      setBookingOpen(false);
      setQuizOpen(false);
    }
  }), [showCart]);
  const cart = useMemo<CartState>(() => {
    // Altijd de actuele productgegevens (prijs, voorraad) uit de catalogus — ook als die
    // na het openen van de site nog van de server binnenkomt.
    // Abonnement uitgezet: geen regel gaat nog als abonnement mee, ook niet naar afrekenen.
    // Kuur vooruit betalen uitgezet: een kuur in de mand valt weg.
    const live = lines.map(l => {
      const product = productById(l.product.id) ?? l.product;
      return {
        ...l,
        product,
        subscribe: l.subscribe && canSubscribe(features, product)
      };
    }).filter(l => features.prepaidKuur || l.product.kind !== 'kuur');
    const subtotal = sum(live);
    const giftSubtotal = sum(live.filter(l => isPhysical(l.product)));
    const hasSubscription = live.some(l => l.subscribe);
    // Alleen cadeaubonnen (digitaal) = niets te verzenden.
    const shipping = giftSubtotal === 0 || giftSubtotal >= policy.freeShippingFrom || hasSubscription ? 0 : policy.shippingCost;
    return {
      lines: live,
      count: live.reduce((n, l) => n + l.qty, 0),
      subtotal,
      giftSubtotal,
      shipping,
      total: (cents(subtotal) + cents(shipping)) / 100,
      hasSubscription,
      unlockedGift: tierIn(giftTiers, giftSubtotal),
      gifts: unlockedIn(giftTiers, policy.freeShippingFrom, giftSubtotal).map(tier => ({
        tier,
        choice: giftChoiceFor(tier, giftChoices)
      })),
      nextGift: nextTierIn(giftTiers, giftSubtotal),
      giftTiers,
      appliedCode,
      adjustments
    };
    // `policy` en `giftTiers` horen erbij: een live wijziging (verversen na 409) rekent de mand opnieuw.
  }, [lines, productById, appliedCode, giftChoices, features, adjustments, policy, giftTiers]);
  const overlays = useMemo<OverlayState>(() => ({
    cartOpen,
    bookingOpen,
    bookingTreatment,
    quizOpen
  }), [cartOpen, bookingOpen, bookingTreatment, quizOpen]);
  return <CartActionsContext.Provider value={cartActions} data-id="element-5512" data-mp="3uvlhh2zhdnnz">
      <OverlayActionsContext.Provider value={overlayActions} data-id="element-5513" data-mp="3uy6l9xjcadod">
        <CartCountContext.Provider value={cart.count} data-id="element-5514" data-mp="mhuwtoszgzjk">
          <CartContext.Provider value={cart} data-id="element-5515" data-mp="rfdb7uprs6yy">
            <OverlayContext.Provider value={overlays} data-id="element-5516" data-mp="rf4wrye00tln">{children}</OverlayContext.Provider>
          </CartContext.Provider>
        </CartCountContext.Provider>
      </OverlayActionsContext.Provider>
    </CartActionsContext.Provider>;
}
function required<T>(value: T | null, hook: string): T {
  if (value === null) throw new Error(`${hook} moet binnen <StoreProvider> gebruikt worden`);
  return value;
}

/** Inhoud en bedragen van de winkelmand. Tekent opnieuw bij elke wijziging in de mand. */
export const useCart = () => required(useContext(CartContext), 'useCart');

/** Alleen het aantal producten — voor badges. */
export const useCartCount = () => required(useContext(CartCountContext), 'useCartCount');

/** Acties op de mand. Stabiel: gebruik dit in kaarten en knoppen. */
export const useCartActions = () => required(useContext(CartActionsContext), 'useCartActions');

/** Welke lade (winkelmand, afspraak, huidadvies) open is. */
export const useOverlays = () => required(useContext(OverlayContext), 'useOverlays');

/** Lades openen en sluiten. Stabiel. */
export const useOverlayActions = () => required(useContext(OverlayActionsContext), 'useOverlayActions');